import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, from, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthServiceService } from '../services/authentication.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class TokenInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private injector: Injector,
    private router: Router
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const excludedUrls = [
      '/oauth2/exchange-code',
      '/account/refresh-token',
      '/account/login',
      '/account/register'
    ];

    const isExcluded = excludedUrls.some(url => request.url.endsWith(url));
    if (isExcluded) {
      return next.handle(request);
    }

    const token = sessionStorage.getItem('access_token');
    const expirationStr = sessionStorage.getItem('token_expiration');
    const now = Date.now();
    const expiration = expirationStr ? parseInt(expirationStr, 10) : null;
    const isExpired = expiration ? now > expiration : true;

    let clonedRequest = request;

    if (token) {
      clonedRequest = this.addToken(request, token);
    }

    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    const authService = this.injector.get(AuthServiceService);

    if (!authService.hasRefreshTokenExpected()) {
      authService.clearStoredAuthState();
      return throwError(() => new Error('Refresh token is not expected for this browser session'));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return from(authService.sendRefreshTokenRequest()).pipe(
        switchMap(success => {
          this.isRefreshing = false;

          if (success) {
            const newToken = sessionStorage.getItem('access_token');
            this.refreshTokenSubject.next(newToken);
            return next.handle(this.addToken(request, newToken!));
          } else {
            authService.logOut();
            return throwError(() => new Error('Refresh token failed'));
          }
        }),
        catchError(err => {
          this.isRefreshing = false;
          authService.logOut();
          return throwError(() => err);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => next.handle(this.addToken(request, token!)))
      );
    }
  }
}
